import { useCallback, useEffect, useState } from 'react';
import { useIsMutating } from '@tanstack/react-query';
import { useMutationState } from '~/hooks/reactQuery/useMutationState';
import { isAbortError, isTimeoutError } from '~/infra/requestErrorKind';
export const useIsAiSearchParsing = (mutationKey)=>useIsMutating({
        mutationKey
    }) > 0;
export const useAiSearchSubmit = (param)=>{
    let { mutationKey, invoke, actions, mapError, cannotExtractCode, timeoutCode, timeoutMs, transformFilter, onExtracted } = param;
    // WHY: render-stable box for the in-flight AbortController — clearFilter cancels through it, which react-query mutations can't do natively.
    const [inFlight] = useState(()=>({
            controller: null
        }));
    const { setAbortInFlight, setFilter, setError } = actions;
    useEffect(()=>()=>{
            var _inFlight_controller;
            return (_inFlight_controller = inFlight.controller) === null || _inFlight_controller === void 0 ? void 0 : _inFlight_controller.abort();
        }, [
        inFlight
    ]);
    const mutation = useMutationState({
        mutationKey,
        mutationFn: async (query)=>{
            var _inFlight_controller;
            (_inFlight_controller = inFlight.controller) === null || _inFlight_controller === void 0 ? void 0 : _inFlight_controller.abort();
            const controller = new AbortController();
            inFlight.controller = controller;
            setAbortInFlight(()=>controller.abort());
            try {
                const response = await invoke({
                    query,
                    signal: controller.signal,
                    timeoutMs
                });
                if (controller.signal.aborted) return {
                    tag: 'aborted'
                };
                if (response.tag === 'cannotExtract') return {
                    tag: 'cannotExtract'
                };
                return {
                    tag: 'extracted',
                    filter: transformFilter ? transformFilter(response.filter) : response.filter
                };
            } catch (err) {
                if (controller.signal.aborted || isAbortError(err)) return {
                    tag: 'aborted'
                };
                return {
                    tag: 'error',
                    code: isTimeoutError(err) ? timeoutCode : mapError(err)
                };
            } finally{
                if (inFlight.controller === controller) {
                    inFlight.controller = null;
                    setAbortInFlight(null);
                }
            }
        },
        onSuccess: (outcome, query)=>{
            if (outcome.tag === 'extracted') {
                setFilter(outcome.filter, query);
                onExtracted === null || onExtracted === void 0 ? void 0 : onExtracted(outcome.filter, query);
            } else if (outcome.tag === 'cannotExtract') {
                setError(cannotExtractCode);
            } else if (outcome.tag === 'error') {
                setError(outcome.code);
            }
        }
    });
    const submit = useCallback(async (rawQuery)=>{
        const query = rawQuery.trim();
        if (query.length === 0) return;
        await mutation.mutateAsync(query).catch(()=>undefined);
    }, [
        mutation
    ]);
    return {
        submit,
        isSubmitting: mutation.isPending
    };
};
